/*
 * product-card-finishing.js
 *
 * Round 13 — unified diagonal-split visual language on PLP cards.
 *
 * MULTI-mode cards (data-card-finishing-mode="multi"):
 *   - Click a .ag-card-finish-swatch =>
 *       1. Mark this swatch .is-selected (clear siblings).
 *       2. Cross-fade the card's main image to the swatch's data-image
 *          (matches the natural .hover-image fade that runs on cards without
 *          our swatches, e.g. Sterling).
 *       3. Update the card's link href to ?variant=<data-variant-id>.
 *       4. e.preventDefault() + e.stopPropagation() so the click doesn't
 *          bubble to the wrapping <a> and navigate the page.
 *
 * SINGLE-mode cards (data-card-finishing-mode="single"):
 *   - Hover a .ag-card-finish-swatch (now a single diagonal-split square,
 *     was two circles pre-Round-13) => show a 200x200 SQUARE preview popover
 *     above. When the swatch has data-has-accent="true", the preview renders
 *     both primary + accent images with clip-path triangles + 1px hairline
 *     along the diagonal. When solid (no accent), the primary fills.
 *
 * Architecture (R12+R13): ALL handlers are document-level event delegation.
 * Survives Shopify collection-filter AJAX re-renders, infinite scroll, search
 * refresh, any other DOM mutation. No per-element binding flags needed.
 *
 * Image-target selector for MULTI swap: .product-item_image:not(.hover-image)
 * — there are TWO <img class="product-item_image"> on each card (main + hover).
 * We only want to mutate the main one; the secondary "hover-image" hover-swap
 * is killed via CSS for cards with .ag-card-finish-list (see CSS file).
 */

(function () {
  'use strict';
  if (window.__agCardFinishingInit) return;
  window.__agCardFinishingInit = true;

  // ---------- Shared popover (single instance appended to body) ----------

  var popoverEl = null;
  var popoverWrap = null;
  var popoverPrimary = null;
  var popoverAccent = null;
  var hoverSupported = window.matchMedia && window.matchMedia('(hover: hover)').matches;
  // Token for stale-fetch cancellation when the user moves quickly between
  // adjacent swatches. Latest hover always wins.
  var popoverToken = 0;

  function ensurePopover() {
    if (popoverEl) return popoverEl;
    popoverEl = document.createElement('div');
    popoverEl.className = 'ag-card-finish-preview';
    popoverEl.hidden = true;
    popoverWrap = document.createElement('span');
    popoverWrap.className = 'ag-card-finish-preview-wrap';
    popoverWrap.dataset.hasAccent = 'false';
    popoverPrimary = document.createElement('img');
    popoverPrimary.className = 'ag-card-finish-preview-img ag-card-finish-preview-img--primary';
    popoverPrimary.alt = '';
    popoverAccent = document.createElement('img');
    popoverAccent.className = 'ag-card-finish-preview-img ag-card-finish-preview-img--accent';
    popoverAccent.alt = '';
    popoverWrap.appendChild(popoverPrimary);
    popoverWrap.appendChild(popoverAccent);
    popoverEl.appendChild(popoverWrap);
    document.body.appendChild(popoverEl);
    return popoverEl;
  }

  function positionPopover(swatchEl) {
    var rect = swatchEl.getBoundingClientRect();
    var pw = popoverEl.offsetWidth || 200;
    var ph = popoverEl.offsetHeight || 200;

    var top = rect.top + window.pageYOffset - ph - 10;
    var left = rect.left + window.pageXOffset + (rect.width / 2) - (pw / 2);

    if (rect.top - ph - 10 < 8) {
      top = rect.bottom + window.pageYOffset + 10;
    }
    var minLeft = window.pageXOffset + 8;
    var maxLeft = window.pageXOffset + window.innerWidth - pw - 8;
    if (left < minLeft) left = minLeft;
    if (left > maxLeft) left = maxLeft;

    popoverEl.style.top = top + 'px';
    popoverEl.style.left = left + 'px';
  }

  // Read primary + accent (large URL + name) from the trigger swatch.
  // Falls back to legacy single-image attrs (data-finish-large / -name) when
  // per-color attrs are missing.
  function readPair(swatchEl) {
    var hasAccent = swatchEl.getAttribute('data-has-accent') === 'true';
    var primaryLarge =
      swatchEl.getAttribute('data-finish-large-primary') ||
      swatchEl.getAttribute('data-finish-large') || '';
    var primaryName =
      swatchEl.getAttribute('data-finish-name-primary') ||
      swatchEl.getAttribute('data-finish-name') || '';
    var accentLarge = hasAccent ? (swatchEl.getAttribute('data-finish-large-accent') || '') : '';
    var accentName = hasAccent ? (swatchEl.getAttribute('data-finish-name-accent') || '') : '';
    return {
      hasAccent: hasAccent && !!accentLarge,
      primaryLarge: primaryLarge,
      primaryName: primaryName,
      accentLarge: accentLarge,
      accentName: accentName
    };
  }

  function showPopover(swatchEl) {
    if (!hoverSupported) return; // touch devices skip
    ensurePopover();
    var pair = readPair(swatchEl);
    if (!pair.primaryLarge) return;

    // No content change — re-show + reposition.
    var samePrimary = popoverPrimary.getAttribute('src') === pair.primaryLarge;
    var sameAccent = !pair.hasAccent || (popoverAccent.getAttribute('src') === pair.accentLarge);
    var sameMode = (popoverWrap.dataset.hasAccent === 'true') === pair.hasAccent;
    if (samePrimary && sameAccent && sameMode) {
      popoverEl.hidden = false;
      positionPopover(swatchEl);
      return;
    }

    // Decode-then-swap: stage primary (and accent if present) offscreen, wait
    // for paint-readiness, then commit. Prevents popover from showing the
    // previous finish during the new image's network fetch on cold cache.
    var token = ++popoverToken;
    var stagingP = new Image();
    if ('fetchPriority' in stagingP) stagingP.fetchPriority = 'high';
    stagingP.src = pair.primaryLarge;

    var stagingA = null;
    if (pair.hasAccent && pair.accentLarge) {
      stagingA = new Image();
      if ('fetchPriority' in stagingA) stagingA.fetchPriority = 'high';
      stagingA.src = pair.accentLarge;
    }

    var swapped = false;
    var swap = function () {
      if (swapped) return;
      if (token !== popoverToken) return;
      swapped = true;
      popoverWrap.dataset.hasAccent = pair.hasAccent ? 'true' : 'false';
      popoverPrimary.src = pair.primaryLarge;
      popoverPrimary.alt = pair.primaryName || '';
      if (pair.hasAccent) {
        popoverAccent.src = pair.accentLarge;
        popoverAccent.alt = pair.accentName || '';
      } else {
        popoverAccent.src = '';
        popoverAccent.alt = '';
      }
      popoverEl.hidden = false;
      positionPopover(swatchEl);
    };

    var pending = stagingA ? 2 : 1;
    var settle = function () { pending -= 1; if (pending <= 0) swap(); };

    if (stagingP.decode) stagingP.decode().then(settle, settle);
    else { stagingP.onload = settle; stagingP.onerror = settle; }
    if (stagingA) {
      if (stagingA.decode) stagingA.decode().then(settle, settle);
      else { stagingA.onload = settle; stagingA.onerror = settle; }
    }
    // R13.1: extended from 300ms → 3000ms safety net. Prior 300ms was firing
    // BEFORE decode completed on cold cache, revealing the popover with
    // stale/empty content (A's reported flash). Combined with the popover-URL
    // = thumb-URL change in product_item.liquid (browser cache serves popover
    // instantly), this should produce zero visible flash on cold or warm.
    setTimeout(swap, 3000);
  }

  function hidePopover() {
    if (popoverEl) popoverEl.hidden = true;
  }

  // ---------- MULTI cross-fade swap (unchanged from R12) ----------

  var SWAP_DURATION_MS = 250;
  function swapImageAnimated(mainImg, hoverImg, newSrc) {
    if (!mainImg || !newSrc) return;
    var currentSrc = mainImg.currentSrc || mainImg.src;
    try {
      if (new URL(currentSrc, window.location.origin).href ===
          new URL(newSrc, window.location.origin).href) {
        return;
      }
    } catch (err) { /* malformed URL — fall through and swap */ }

    if (!hoverImg) {
      mainImg.style.opacity = '0';
      setTimeout(function () {
        mainImg.src = newSrc;
        if (mainImg.hasAttribute('srcset')) mainImg.removeAttribute('srcset');
        requestAnimationFrame(function () { mainImg.style.opacity = '1'; });
      }, SWAP_DURATION_MS);
      return;
    }

    hoverImg.src = newSrc;
    if (hoverImg.hasAttribute('srcset')) hoverImg.removeAttribute('srcset');
    hoverImg.style.setProperty('opacity', '1', 'important');

    setTimeout(function () {
      hoverImg.style.transition = 'none';
      mainImg.src = newSrc;
      if (mainImg.hasAttribute('srcset')) mainImg.removeAttribute('srcset');
      hoverImg.style.removeProperty('opacity');
      void hoverImg.offsetHeight;
      hoverImg.style.transition = '';
    }, SWAP_DURATION_MS);
  }

  // ---------- Delegated MULTI handler (click) ----------
  document.addEventListener('click', function (e) {
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    var listEl = sw.closest('.ag-card-finish-list[data-card-finishing-mode="multi"]');
    if (!listEl) return;

    e.preventDefault();
    e.stopPropagation();

    listEl.querySelectorAll('.ag-card-finish-swatch').forEach(function (s) {
      s.classList.toggle('is-selected', s === sw);
    });

    var card = listEl.closest('.product-item_component') || listEl.parentElement;
    if (!card) return;

    var img = card.querySelector('.product-item_image:not(.hover-image)') ||
              card.querySelector('.product-item_image');
    var hoverImg = card.querySelector('.product-item_image.hover-image');
    var link = card.querySelector('a.product-item_link') ||
               card.querySelector('a.product-item_image-wrapper') ||
               card.querySelector('a[href*="/products/"]');

    var src = sw.getAttribute('data-image');
    swapImageAnimated(img, hoverImg, src);

    var vid = sw.getAttribute('data-variant-id');
    if (vid && link && link.href) {
      try {
        var u = new URL(link.href, window.location.origin);
        u.searchParams.set('variant', vid);
        link.href = u.toString();
      } catch (err) {
        var sep = link.href.indexOf('?') === -1 ? '?' : '&';
        var stripped = link.href.replace(/[?&]variant=\d+/g, '');
        link.href = stripped + sep + 'variant=' + vid;
      }
    }
  });

  // ---------- Delegated SINGLE handler (hover popover) ----------
  // Round 13: target switched from .ag-finish-circle (two circles) to
  // .ag-card-finish-swatch inside data-card-finishing-mode="single" list
  // (single diagonal-split swatch). Same delegation pattern as MULTI click.
  // mouseover/mouseout bubble (mouseenter/mouseleave do not), so we can use
  // them for delegation. relatedTarget check prevents flicker when moving
  // across child elements inside the swatch.
  document.addEventListener('mouseover', function (e) {
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    var listEl = sw.closest('.ag-card-finish-list[data-card-finishing-mode="single"]');
    if (!listEl) return;
    if (e.relatedTarget && sw.contains(e.relatedTarget)) return;
    showPopover(sw);
  });

  document.addEventListener('mouseout', function (e) {
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    if (e.relatedTarget && sw.contains(e.relatedTarget)) return;
    hidePopover();
  });

  // Keyboard accessibility — focusin/focusout bubble (focus/blur do not).
  document.addEventListener('focusin', function (e) {
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    var listEl = sw.closest('.ag-card-finish-list[data-card-finishing-mode="single"]');
    if (!listEl) return;
    showPopover(sw);
  });

  document.addEventListener('focusout', function (e) {
    var sw = e.target.closest('.ag-card-finish-swatch');
    if (!sw) return;
    hidePopover();
  });
})();
